import { Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, signal, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ParkingZoneDto, ParkingZoneService, AddImageToParkingZoneRequest, ImageDto } from '../../../api/gen';
import { JJamesAlertComponent, JJamesBadgeComponent, JJamesImageContainerComponent, JjamesButtonComponent, BadgeVariant, ResponsiveBaseComponent, Role } from 'shared-lib';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { RoleService } from '../../auth/role-service';

@Component({
  selector: 'app-parking-zone-item',
  imports: [CommonModule, JJamesAlertComponent, JJamesBadgeComponent, JJamesImageContainerComponent, JjamesButtonComponent, MatSnackBarModule, MatProgressSpinnerModule],
  templateUrl: './parking-zone-item.component.html',
  styleUrl: './parking-zone-item.component.scss'
})
export class ParkingZoneItemComponent extends ResponsiveBaseComponent implements OnInit, OnChanges {
  private parkingZoneService = inject(ParkingZoneService);
  private snackBar = inject(MatSnackBar);
  private roleService = inject(RoleService);

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  @Input({ required: true }) zone!: ParkingZoneDto;
  @Output() detailsClicked = new EventEmitter<string>();
  @Output() editClicked = new EventEmitter<string>();
  @Output() deleteClicked = new EventEmitter<string>();
  @Output() unassignClicked = new EventEmitter<string>();
  @Output() imageUploaded = new EventEmitter<ImageDto>();

  showValidationErrors = false;
  isUploading = signal(false);
  isLoadingImages = signal(false);
  zoneImages = signal<ImageDto[]>([]);
  isAdmin = signal(false);

  async ngOnInit(): Promise<void> {
    this.loadZoneImages();
    this.isAdmin.set(await this.roleService.checkAccess(Role.Administrator));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['zone'] && !changes['zone'].firstChange) {
      this.loadZoneImages();
    }
  }

  private loadZoneImages(): void {
    if (!this.zone?.id) {
      return;
    }

    this.isLoadingImages.set(true);
    this.parkingZoneService.parkingZoneParkingZoneIdImagesGet(this.zone.id).subscribe({
      next: (images) => {
        this.zoneImages.set(images);
        this.isLoadingImages.set(false);
      },
      error: (error) => {
        console.error('Error loading zone images:', error);
        this.zoneImages.set([]);
        this.isLoadingImages.set(false);
      }
    });
  }

  onDetailsClick() {
    if (this.zone.id) {
      this.detailsClicked.emit(this.zone.id);
    }
  }

  onEditClick() {
    if (this.zone.id) {
      this.editClicked.emit(this.zone.id);
    }
  }

  onDeleteClick() {
    if (this.zone.id) {
      this.deleteClicked.emit(this.zone.id);
    }
  }

  onUnassignClick() {
    if (this.zone.id) {
      this.unassignClicked.emit(this.zone.id);
    }
  }

  onInfoClick(event: Event) {
    event.stopPropagation();
    this.showValidationErrors = true;
  }

  onCloseAlert() {
    this.showValidationErrors = false;
  }

  getGovernanceTypeLabel(): string {
    return this.zone.governanceType === 'Internal' ? 'Internal' : 'External';
  }

  getGovernanceTypeClass(): BadgeVariant {
    return this.zone.governanceType === 'Internal' ? 'success' : 'info';
  }

  getZoneImage(): string | undefined {
    // Return the primary image URL or first image if available from loaded images
    const images = this.zoneImages();
    const primaryImage = images.find(img => img.isPrimary);
    if (primaryImage?.fileUrl) {
      return primaryImage.fileUrl;
    }
    return images[0]?.fileUrl ?? undefined;
  }

  onUploadClick(): void {
    this.fileInput?.nativeElement?.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file || !this.zone.id) {
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      this.snackBar.open('Please select a valid image file (JPEG, PNG, GIF, or WebP)', 'Close', {
        duration: 5000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      this.snackBar.open('Image file size must be less than 5MB', 'Close', {
        duration: 5000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    this.uploadImage(file);
  }

  private uploadImage(file: File): void {
    this.isUploading.set(true);

    const reader = new FileReader();
    reader.onload = () => {
      const base64String = reader.result as string;
      // Remove the data URL prefix (e.g., "data:image/png;base64,")
      const base64Data = base64String.split(',')[1];

      const request: AddImageToParkingZoneRequest = {
        fileName: file.name,
        base64Data: base64Data,
        isPrimary: true // Set as primary if no images exist
      };

      this.parkingZoneService.parkingZoneParkingZoneIdImagesPost(this.zone.id!, request).subscribe({
        next: (imageDto) => {
          this.isUploading.set(false);
          this.snackBar.open('Image uploaded successfully', 'Close', {
            duration: 3000
          });
          // Add the new image to the local images array
          this.zoneImages.update(images => [...images, imageDto]);
          this.imageUploaded.emit(imageDto);
          // Reset file input
          if (this.fileInput?.nativeElement) {
            this.fileInput.nativeElement.value = '';
          }
        },
        error: (error) => {
          this.isUploading.set(false);
          console.error('Error uploading image:', error);
          this.snackBar.open('Failed to upload image. Please try again.', 'Close', {
            duration: 5000,
            panelClass: ['error-snackbar']
          });
        }
      });
    };

    reader.onerror = () => {
      this.isUploading.set(false);
      this.snackBar.open('Failed to read image file', 'Close', {
        duration: 5000,
        panelClass: ['error-snackbar']
      });
    };

    reader.readAsDataURL(file);
  }
}
